/**
 * Navigation utilities for Support Tools
 * Uses Zod for validating route parameters from TanStack Router
 */

import type { NavigateOptions } from "@tanstack/react-router";
import { z } from "zod";
import { SUPPORT_TOOLS } from "./constants";

// Extract tool IDs from the constants for type safety
const TOOL_IDS = SUPPORT_TOOLS.map((tool) => tool.id);

const toolEnum =
  TOOL_IDS.length > 0
    ? z.enum(TOOL_IDS as [string, ...string[]])
    : z.string().superRefine((value, ctx) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Navigation: No support tools are configured, so "${value}" cannot be resolved.`,
        });
      });

// Zod schema for validating tool route parameters
export const toolRouteParamsSchema = z.object({
  tool: toolEnum,
});

export type ToolRouteParams = z.infer<typeof toolRouteParamsSchema>;

// Type for valid tool IDs
export type ValidToolId = (typeof TOOL_IDS)[number];

/**
 * Get navigation options for a support tool using dynamic $tool route
 * @param toolId - The tool identifier (must be a valid tool ID)
 * @param orgId - Organization ID (if in org context)
 */
export const getToolNavigation = (toolId: ValidToolId, orgId?: string): NavigateOptions => {
  // Find the tool to get its path
  const tool = SUPPORT_TOOLS.find((t) => t.id === toolId);

  if (!tool) {
    // Fallback to home page for invalid tools
    return {
      to: orgId ? "/orgs/$orgId/support-tools" : "/support-tools",
      params: orgId ? { orgId } : undefined,
    };
  }

  // Use dynamic $tool route pattern
  if (orgId) {
    return {
      to: "/orgs/$orgId/support-tools/$tool",
      params: { orgId, tool: tool.path },
    };
  } else {
    return {
      to: "/support-tools/$tool",
      params: { tool: tool.path },
    };
  }
};

/**
 * Get the correct Support Tools home path based on context
 * @param orgId - Organization ID if in org context
 * @returns Path to support tools home page
 */
export const getSupportToolsHomePath = (orgId?: string): string => {
  return orgId ? `/orgs/${orgId}/support-tools` : "/support-tools";
};
